import React, { useContext, useEffect, useMemo, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonSpinner,
  IonText,
  IonToolbar,
  IonTitle,
  useIonAlert,
  useIonToast,
  IonAvatar,
  useIonViewWillLeave,
} from "@ionic/react";
import { arrowBackOutline, createOutline } from "ionicons/icons";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { ProfileContext } from "../context/ProfileContext";
import "../theme/AppStyles.css";
import { FB_COLLECTION_PATH } from "../functions/postActionTypes";
import {
  markAnnouncementAsRead,
  softDeleteAnnouncement,
} from "../domain/announcements/announcementActions";

type AnnouncementDetailRouteParams = {
  id: string;
};

type AnnouncementDoc = {
  announcementId?: string;
  authorUid: string;
  authorName: string;
  authorCompany: string;
  authorProfilePicURL?: string;
  title: string;
  body: string;
  imageUrl: string | null;
  recipientUids: string[];
  selectedCrewIds?: string[];
  isPinned: boolean;
  createdAt: number;
  updatedAt?: number;
  status?: string;
};

const AnnouncementDetail: React.FC<RouteComponentProps<AnnouncementDetailRouteParams>> = ({ match }) => {
  const history = useHistory<any>();
  const { profile } = useContext(ProfileContext);
  const [presentAlert] = useIonAlert();
  const [presentToast] = useIonToast();

  const [announcement, setAnnouncement] = useState<AnnouncementDoc | null>(null);
  const [loading, setLoading] = useState(true);

  const announcementId = String(match?.params?.id || "");
  const isSchedulerMode = profile?.UserLastRoleScheduler === true;

  const blurFocusedElement = () => {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && activeElement !== document.body) {
      activeElement.blur();
    }
  };

  useIonViewWillLeave(() => {
    blurFocusedElement();
  });

  const isAuthor = useMemo(() => {
    return (
      String(announcement?.authorUid || "") !== "" &&
      String(announcement?.authorUid || "") === String(profile?.UserId || "")
    );
  }, [announcement?.authorUid, profile?.UserId]);

  const canManageAnnouncement = isSchedulerMode && isAuthor;

  const displayDate = useMemo(() => {
    const dateMs = Number(announcement?.createdAt) || 0;
    if (!dateMs) return "";

    return new Date(dateMs)
      .toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
      .replace(",", " •");
  }, [announcement?.createdAt]);

  useEffect(() => {
    if (!announcementId) {
      setAnnouncement(null);
      setLoading(false);
      return;
    }

    setLoading(true);

      const ref = doc(db, FB_COLLECTION_PATH.Announcements, announcementId);

    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setAnnouncement(null);
          setLoading(false);
          return;
        }

        const data: any = snap.data() || {};

        if (String(data?.status || "active") === "deleted") {
          setAnnouncement(null);
          setLoading(false);
          return;
        }

        setAnnouncement({
          announcementId: snap.id,
          authorUid: data?.authorUid || "",
          authorName: data?.authorName || "",
          authorCompany: data?.authorCompany || "",
          authorProfilePicURL: data?.authorProfilePicURL || "../assets/profilePic.png",
          title: data?.title || "",
          body: data?.body || "",
          imageUrl: data?.imageUrl ?? null,
          recipientUids: Array.isArray(data?.recipientUids) ? data.recipientUids : [],
          selectedCrewIds: Array.isArray(data?.selectedCrewIds) ? data.selectedCrewIds : [],
          createdAt: Number(data?.createdAt) || 0,
          updatedAt:
            data?.updatedAt === undefined || data?.updatedAt === null ? undefined : Number(data?.updatedAt) || 0,
          isPinned: data?.isPinned === true,
          status: data?.status || "active",
        });

        setLoading(false);
      },
      (_error) => {
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [announcementId]);

  const handleDelete = async () => {
    const id = announcement?.announcementId;
    if (!id || !canManageAnnouncement) return;

    await presentAlert({
      header: "Delete Announcement",
      message: "Are you sure you want to delete this announcement?",
      buttons: [
        { text: "Cancel", role: "cancel" },
        {
          text: "Delete",
          role: "destructive",
          handler: async () => {
            try {
              await softDeleteAnnouncement(id);

              await presentToast({
                message: "Announcement deleted",
                duration: 1400,
                position: "top",
                color: "medium",
              });

              history.replace("/Chats");
            } catch (error) {
              // console.log("handleDelete error:", error);
              await presentAlert({
                header: "Error",
                message: "Unable to delete this announcement.",
                buttons: ["OK"],
              });
            }
          },
        },
      ],
    });
  };

  const handleBack = () => {
    blurFocusedElement();
    if (history.location.state?.cameFrom === "Chats") {
      history.replace("/Chats");
      return;
    }

    history.goBack();
  };

  const handleEdit = () => {
    if (!announcement?.announcementId || !canManageAnnouncement) return;

    history.push({
      pathname: "/Announcement",
      state: {
        cameFrom: "AnnouncementDetail",
        mode: "edit",
        announcementId: announcement.announcementId,
        recipientUids: Array.isArray(announcement.recipientUids) ? announcement.recipientUids : [],
        selectedCrewIds: Array.isArray(announcement.selectedCrewIds) ? announcement.selectedCrewIds : [],
        draftTitle: announcement.title || "",
        draftBody: announcement.body || "",
        draftImageUrl: announcement.imageUrl || "",
        draftIsPinned: Boolean(announcement.isPinned),
      },
    });
  };

  useEffect(() => {
    const uid = profile?.UserId;
    if (!uid || !announcementId) return;

    const markRead = async () => {
      try {
        await markAnnouncementAsRead({
          userId: uid,
          announcementId,
        });
      } catch (error) {
        // console.log("mark announcement read error:", error);
      }
    };

    markRead();
  }, [profile?.UserId, announcementId]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          color={isSchedulerMode ? "secondarywarning" : "warning"}
        >
          <IonButtons
            slot="start"
          >
            <IonButton
              onClick={handleBack}
            >
              <IonIcon
                icon={arrowBackOutline}
                slot="start"
              />
              Back
            </IonButton>
          </IonButtons>

          <IonTitle>
            Announcement
          </IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent
        fullscreen={true}
      >
        {loading && (
          <div style={{ padding: 28, display: "flex", justifyContent: "center" }}>
            <IonSpinner />
          </div>
        )}

        {!loading && !announcement && (
          <div
            style={{
              maxWidth: "820px",
              margin: "0 auto",
              padding: "20px 16px 28px",
            }}
          >
            <div
              style={{
                background: "var(--ion-item-background, var(--ion-background-color))",
                borderRadius: "14px",
                padding: "18px",
                border: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <IonText>
                <h2 style={{ marginTop: 0, marginBottom: 8 }}>Announcement not found</h2>
              </IonText>

              <IonText
                color="medium"
              >
                <p style={{ margin: 0 }}>This announcement may have been deleted or is no longer available.</p>
              </IonText>
            </div>
          </div>
        )}

        {!loading && announcement && (
          <div
          // style={{
          //   maxWidth: "820px",
          //   margin: "0 auto",
          //   padding: "12px 12px 28px",
          // }}
          >
            <div
              style={{
                background: "var(--ion-item-background, var(--ion-background-color))",
                borderRadius: "14px",
                padding: "18px",
                border: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                {/* AVATAR */}
                <IonAvatar
                  style={{ width: 44, height: 44, flexShrink: 0 }}
                >
                  <img
                    src={announcement.authorProfilePicURL || "../assets/profilePic.png"}
                    alt={announcement.authorName || "Author"}
                    style={{ objectFit: "cover" }}
                  />
                </IonAvatar>

                {/* AUTHOR INFO */}
                <div style={{ flex: 1, minWidth: 0, paddingRight: 110 }}>
                  {/* NAME */}
                  <IonText>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: "0.98rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {announcement.authorName || "Unknown Author"}
                    </div>
                  </IonText>

                  {/* COMPANY */}
                  {!!announcement.authorCompany && (
                    <IonText
                      color="medium"
                    >
                      <div
                        style={{
                          fontSize: "0.88rem",
                          marginTop: 2,
                        }}
                      >
                        {announcement.authorCompany}
                      </div>
                    </IonText>
                  )}
                </div>

                {/* DATE TOP RIGHT */}
                <IonText
                  color="medium"
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 2,
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayDate}
                </IonText>
              </div>

              {!!announcement.title && (
                <IonText>
                  <h2
                    style={{
                      margin: "8px 0 10px 0",
                      fontSize: "1.08rem",
                      lineHeight: 1.3,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {announcement.title || "Untitled Announcement"}
                  </h2>
                </IonText>
              )}

              <IonText>
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.6,
                    fontSize: "0.98rem",
                    marginBottom: announcement.imageUrl ? 14 : 0,
                  }}
                >
                  {announcement.body || ""}
                </div>
              </IonText>

              {!!announcement.imageUrl && (
                <div style={{ marginTop: 6 }}>
                  <img
                    src={announcement.imageUrl}
                    alt="Announcement"
                    style={{
                      width: "100%",
                      maxHeight: "420px",
                      objectFit: "cover",
                      borderRadius: "14px",
                      display: "block",
                    }}
                    onError={(e) => {
                      const el = e.currentTarget as HTMLImageElement;
                      el.style.display = "none";
                    }}
                  />
                </div>
              )}

              {canManageAnnouncement && (
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    marginTop: 16,
                    paddingTop: 14,
                    borderTop: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <IonButton
                    size="small"
                    fill="outline"
                    color={profile?.UserLastRoleScheduler ? "secondarywarning" : "warning"}
                    onClick={handleEdit}
                  >
                    <IonIcon
                      icon={createOutline}
                      slot="start"
                    />
                    Edit
                  </IonButton>

                  <IonButton
                    size="small"
                    fill="outline"
                    color="danger"
                    onClick={handleDelete}
                  >
                    Delete
                  </IonButton>
                </div>
              )}
            </div>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default AnnouncementDetail;
